import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Column from './Column';
import TaskModal from './TaskModal';
import Header from './Header';
import BoardSelector from './BoardSelector';
import BoardManager from './BoardManager';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus } = FiIcons;

const KanbanBoard = ({ 
  board, 
  boards, 
  currentBoardId, 
  onBoardSwitch, 
  onCreateBoard, 
  onDeleteBoard, 
  onUpdateBoard 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState('backlog');
  const [showBoardManager, setShowBoardManager] = useState(false);
  const navigate = useNavigate();

  const currentBoard = board || { columns: {} };

  const addTask = (taskData) => {
    const newTask = {
      id: Date.now().toString(),
      ...taskData,
      createdAt: new Date().toISOString()
    };

    const updatedBoard = {
      ...currentBoard,
      columns: {
        ...currentBoard.columns,
        [taskData.columnId]: {
          ...currentBoard.columns[taskData.columnId],
          tasks: [...currentBoard.columns[taskData.columnId].tasks, newTask]
        }
      }
    };

    onUpdateBoard(currentBoardId, updatedBoard);
  };

  const updateTask = (taskData) => {
    const newColumns = { ...currentBoard.columns };
    
    // Remove task from all columns first
    Object.keys(newColumns).forEach(columnId => {
      newColumns[columnId] = {
        ...newColumns[columnId],
        tasks: newColumns[columnId].tasks.filter(task => task.id !== taskData.id)
      };
    });

    // Add updated task to the specified column
    newColumns[taskData.columnId] = {
      ...newColumns[taskData.columnId],
      tasks: [...newColumns[taskData.columnId].tasks, { ...taskData, updatedAt: new Date().toISOString() }]
    };

    const updatedBoard = { ...currentBoard, columns: newColumns };
    onUpdateBoard(currentBoardId, updatedBoard);
  };

  const deleteTask = (taskId) => {
    const newColumns = { ...currentBoard.columns };
    Object.keys(newColumns).forEach(columnId => {
      newColumns[columnId] = {
        ...newColumns[columnId],
        tasks: newColumns[columnId].tasks.filter(task => task.id !== taskId)
      };
    });

    const updatedBoard = { ...currentBoard, columns: newColumns };
    onUpdateBoard(currentBoardId, updatedBoard);
  };

  const moveTask = (taskId, sourceColumnId, targetColumnId, targetIndex) => {
    if (sourceColumnId === targetColumnId) return;

    const newColumns = { ...currentBoard.columns };
    
    // Find and remove the task from source column
    const sourceColumn = newColumns[sourceColumnId];
    const taskToMove = sourceColumn.tasks.find(task => task.id === taskId);
    
    if (!taskToMove) return;

    newColumns[sourceColumnId] = {
      ...sourceColumn,
      tasks: sourceColumn.tasks.filter(task => task.id !== taskId)
    };

    // Add task to target column
    const targetColumn = newColumns[targetColumnId];
    const updatedTask = { ...taskToMove, updatedAt: new Date().toISOString() };
    
    newColumns[targetColumnId] = {
      ...targetColumn,
      tasks: [...targetColumn.tasks.slice(0, targetIndex), updatedTask, ...targetColumn.tasks.slice(targetIndex)]
    };

    const updatedBoard = { ...currentBoard, columns: newColumns };
    onUpdateBoard(currentBoardId, updatedBoard);
  };

  const openModal = (columnId = 'backlog', task = null) => {
    setSelectedColumn(columnId);
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTask(null);
    setSelectedColumn('backlog');
  };

  const handleSubmit = (taskData) => {
    if (editingTask) {
      updateTask({ ...taskData, id: editingTask.id });
    } else {
      addTask(taskData);
    }
    closeModal();
  };

  const handleBoardSwitch = (boardId) => {
    onBoardSwitch(boardId);
    navigate(`/`);
  };

  const handleCreateBoard = (boardName) => {
    const newBoardId = onCreateBoard(boardName);
    setShowBoardManager(false);
    navigate(`/`);
  };

  const handleDeleteBoard = (boardId) => {
    if (onDeleteBoard(boardId)) {
      setShowBoardManager(false);
    }
  };

  const handleUpdateBoardName = (boardId, newName) => {
    if (newName.trim() && newName !== boards[boardId].name) {
      const updatedBoards = {
        ...boards,
        [boardId]: {
          ...boards[boardId],
          name: newName.trim()
        }
      };
      // Update boards state
      onUpdateBoard(boardId, updatedBoards[boardId]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header 
        onAddTask={() => openModal()} 
        currentBoard={currentBoard}
        onBoardManagerOpen={() => setShowBoardManager(true)}
      />
      
      {/* Board Selector Bar */}
      <BoardSelector
        boards={boards}
        currentBoardId={currentBoardId}
        onBoardSwitch={handleBoardSwitch}
        onBoardManagerOpen={() => setShowBoardManager(true)}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <AnimatePresence>
            {Object.values(currentBoard.columns || {}).map((column) => (
              <motion.div
                key={column.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Column
                  column={column}
                  onAddTask={() => openModal(column.id)}
                  onEditTask={(task) => openModal(column.id, task)}
                  onDeleteTask={deleteTask}
                  onMoveTask={moveTask}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <TaskModal
            isOpen={isModalOpen}
            onClose={closeModal}
            onSubmit={handleSubmit}
            columns={currentBoard.columns || {}}
            selectedColumn={selectedColumn}
            editingTask={editingTask}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBoardManager && (
          <BoardManager
            boards={boards}
            currentBoardId={currentBoardId}
            onClose={() => setShowBoardManager(false)}
            onBoardSwitch={handleBoardSwitch}
            onCreateBoard={handleCreateBoard}
            onDeleteBoard={handleDeleteBoard}
            onUpdateBoard={handleUpdateBoardName}
          />
        )}
      </AnimatePresence>

      {/* Floating Add Button */}
      <motion.button
        onClick={() => openModal()}
        className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <SafeIcon icon={FiPlus} className="w-6 h-6" />
      </motion.button>
    </div>
  );
};

export default KanbanBoard;